/**
 * 路由由注册表派生（src/shell/registry.ts）—— 静态元数据与页面加载器分别维护，由 RoutePath 检查完整映射。
 *
 * 外壳两套并存到 M5（规格 §13）：默认新外壳 AppShell；「旧版布局」开关打开时
 * 回到旧侧栏布局，供回退测试（规格 §8.3）。M5 经用户确认后删除旧布局与开关。
 */
import { VAULT_WINDOW_PATH } from '@/features/stock-vault/global/window-bridge';
import { GlobalVault } from '@/features/stock-vault/global/GlobalVault';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { LoadingScreen } from '@/components/ui/loading';
import { AppShell } from '@/shell/AppShell';
import { HOME_PATH, REDIRECTS, ROUTES } from '@/shell/registry';
import { ROUTE_LOADERS } from '@/shell/route-loaders';
import { PageErrorBoundary } from './PageErrorBoundary';
import { useShellStore } from '@/shell/shell-store';

// The legacy shell includes chart dependencies; load it only when selected.
const MainLayout = lazy(() => import('@/components/layout/main-layout').then(m => ({ default: m.MainLayout })));

const DetachedVault=lazy(()=>import('@/features/stock-vault/global/VaultWindow'));
function VaultLauncher(){const location=useLocation();return location.pathname.replace(/\/$/,'')===VAULT_WINDOW_PATH?null:<GlobalVault/>;}

const PAGES = ROUTES.map((r) => ({ path: r.path, Page: lazy(ROUTE_LOADERS[r.path]) }));

/** 重定向保留查询参数与 hash（旧 <Navigate> 会把 ?sector=… 丢掉） */
function RedirectKeepingQuery({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

// 获取 base path (用于子路径部署)
const basename = import.meta.env.BASE_URL || '/';

export function AppRouter() {
  const legacy = useShellStore((s) => s.legacy);
  return (
    <BrowserRouter basename={basename}>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path={VAULT_WINDOW_PATH} element={<PageErrorBoundary key="detached-vault"><DetachedVault/></PageErrorBoundary>}/>
          <Route element={legacy ? <MainLayout /> : <AppShell />}>
            {/* 默认落点资金监控（2026-09-18 用户点名） */}
            <Route path="/" element={<Navigate to={HOME_PATH} replace />} />
            {PAGES.map(({ path, Page }) => (
              <Route key={path} path={path} element={<PageErrorBoundary key={path}><Page /></PageErrorBoundary>} />
            ))}
            {Object.entries(REDIRECTS).map(([from, to]) => (
              <Route key={from} path={from} element={<RedirectKeepingQuery to={to} />} />
            ))}
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <VaultLauncher />
    </BrowserRouter>
  );
}
